/** @format */

import useCardGallery from '@/@ducks/cardRoom/hooks/useCardGallery';
import GalleryIcon from '@/assets/icons/card-room-gallery-icon.svg';
import MapIcon from '@/assets/icons/card-room-map-pin-icon.svg';
import PlanimetryIcon from '@/assets/icons/card-room-planimetry-icon.svg';
import VideoIcon from '@/assets/icons/card-room-video-icon.svg';
import VrIcon from '@/assets/icons/card-room-vr-icon.svg';
import ChevronRight from '@/assets/icons/joivy_icon_chevron_right_light.svg';
import CloseIcon from '@/assets/icons/joivy_plp_close.svg';
import { PrimaryButton } from '@/components/page/PageListing/components/shared.style';
import { TTranslation, useAppTranslation } from '@/i18n/declarations';
import { Link } from 'gatsby-plugin-intl';
import React, { Dispatch, FunctionComponent as FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SwiperType, { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import { useToggleZendeskWidget } from '../../../../@ducks/general/hooks/useToggleZendeskWidget';
import { Icon } from '../../Icon';
import Details from '../Details';
import { SwiperButtonNext, SwiperButtonPrev } from '../GalleryCarousel.style';
import Price from '../Price';
import { useCardRoomContext } from '../context';
import { Title } from '../index.style';
import { ImgSlide } from './ImageSlide';
import { MapSlide } from './MapSlide';
import { TabInfo } from './TabInfo';
import { VRSlide } from './VRSlide';
import { VideoSlide } from './VideoSlide';
import {
  CloseButton,
  Content,
  Desktop,
  Footer,
  Header,
  HeaderDetails,
  HeaderRight,
  Mobile,
  TabButton,
  TabControls,
  Wrapper,
} from './index.style';
import { Tab } from './types';

/*
 NOTES:
 - Indexes logic might seem odd, but it's been done with the (hopefully correct) assumption that
   there will always be only one map (and it will ALWAYS be present) and one vr, while photos,
   videos, and planimetry might be mutiple.
 - All the information about what tabs are visibile is controlled in the parent component, since the
   same tabs array is uded to display the buttons to open the modal in the correct position.
*/

export const TabsModal: FC<TabsModalProps> = ({ tabs, activeTab, switchTab, close }) => {
  const { t } = useAppTranslation();
  useToggleZendeskWidget(true);

  const { title, url, room } = useCardRoomContext();
  const { code, media } = room!;

  const {
    photosHighQuality: photos,
    urlVR,
    urlPlanimetry,
  } = useCardGallery({
    media,
  });

  const [currentSlide, setCurrentSlide] = useState<number>(1);
  const [isVRPlaying, setIsVRPlaying] = useState(false);
  const [isMapActive, setIsMapActive] = useState(false);

  const updateCounter = (swiper: SwiperType) => {
    const index = swiper.realIndex + 1;
    if (tabs.includes('map') && index === startMapIndex) switchTab('map');
    if (tabs.includes('vr') && index === startVrIndex) switchTab('vr');
    if (tabs.includes('gallery') && index >= startGalleryIndex! && index <= photos.length) switchTab('gallery');
    if (tabs.includes('planimetry') && index === startPlanimetryIndex!) switchTab('planimetry');
    setCurrentSlide(index);
  };

  const getTabInfo = useCallback(
    (tab: Tab) => {
      switch (tab) {
        case 'gallery':
          return {
            label: t('plp.card.modal.tabs.gallery' as TTranslation, { photos: photos.length }),
            icon: GalleryIcon,
          };
        case 'videos':
          return { label: t('plp.card.modal.tabs.videos' as TTranslation, { videos: media.length }), icon: VideoIcon };
        case 'planimetry':
          return {
            label: t('plp.card.modal.tabs.planimetry' as TTranslation, { floorplans: 1 }),
            icon: PlanimetryIcon,
          };
        case 'vr':
          return { label: t('plp.card.modal.tabs.vr' as TTranslation), icon: VrIcon };
        case 'map':
          return { label: t('plp.card.modal.tabs.map' as TTranslation), icon: MapIcon };
      }
    },
    [media],
  );

  const swiperSlides = useMemo(() => {
    const slides = [];

    if (tabs.includes('gallery')) {
      photos.forEach((photo, index) => {
        slides.push(<ImgSlide photo={photo} index={index} />);
      });
    }
    if (tabs.includes('videos')) slides.push(<VideoSlide url={urlVR!} />);
    if (tabs.includes('planimetry')) slides.push(<ImgSlide photo={urlPlanimetry!} index={0} />);
    if (tabs.includes('vr'))
      slides.push(<VRSlide backgroundPicture={photos[0]} url={urlVR!} vrState={[isVRPlaying, setIsVRPlaying]} />);
    slides.push(<MapSlide mapState={[isMapActive, setIsMapActive]} />);

    return slides;
  }, [tabs, photos, urlVR]);

  const swiper = useRef<SwiperRef>(null);

  const startGalleryIndex = useMemo(() => {
    if (tabs.includes('gallery')) return 1;
    return null;
  }, [tabs]);

  // REFACTOR WHEN VIDEOS WILL BE AVAILABLE
  const startVideosIndex = useMemo(() => {
    return null;
  }, [tabs]);

  const startPlanimetryIndex = useMemo(() => {
    if (tabs.includes('planimetry')) return tabs.includes('gallery') ? photos.length + 1 : 1;
    return null;
  }, [tabs]);

  const startVrIndex = useMemo(() => {
    if (tabs.includes('vr')) return swiperSlides.length - 1;
    return null;
  }, [tabs]);

  const startMapIndex = useMemo(() => {
    if (tabs.includes('map')) return swiperSlides.length;
    return null;
  }, [tabs]);

  useEffect(() => {
    if (!swiper.current) return;
    const index = currentSlide;

    if (activeTab === 'gallery' && startGalleryIndex && index > photos.length)
      swiper.current.swiper.slideTo(startGalleryIndex);
    if (activeTab === 'videos' && startVideosIndex) swiper.current.swiper.slideTo(startVideosIndex); // REFACTOR WHEN VIDEOS WILL BE AVAILABLE
    if (activeTab === 'planimetry' && startPlanimetryIndex) swiper.current.swiper.slideTo(startPlanimetryIndex);
    if (activeTab === 'vr' && startVrIndex && index !== startVrIndex) swiper.current.swiper.slideTo(startVrIndex);
    if (activeTab === 'map' && startMapIndex && index !== startMapIndex) swiper.current.swiper.slideTo(startMapIndex);
  }, [activeTab, swiper, startGalleryIndex, startVideosIndex, startPlanimetryIndex, startVrIndex, startMapIndex]);

  const noSwipe = useMemo(() => {
    if (swiperSlides.length === 1) return true;
    if (currentSlide === startMapIndex && isMapActive) return true;
    if (currentSlide === startVrIndex && isVRPlaying) return true;
    return false;
  }, [currentSlide, isMapActive, isVRPlaying]);

  return (
    <Wrapper>
      <Header>
        <div>
          <Title as="p" $fullWidth>
            {title}
          </Title>
          <HeaderDetails>
            <Price hideDateDetails />
            <span className="divider"></span>
            <Details />
          </HeaderDetails>
        </div>
        <HeaderRight>
          <Desktop>
            <DetailsButton url={url} />
          </Desktop>
          <CloseButton onClick={close}>
            <Icon icon={CloseIcon} width={20} height={20} />
          </CloseButton>
        </HeaderRight>
      </Header>
      <Content>
        <Swiper
          ref={swiper}
          navigation={{
            nextEl: `.swiper-button-next-${code}`,
            prevEl: `.swiper-button-prev-${code}`,
          }}
          slidesPerView={1}
          loop={true}
          modules={[Navigation]}
          onSlideChange={updateCounter}
        >
          {swiperSlides.map((slide, index) => (
            <SwiperSlide key={index + code} className={noSwipe ? 'swiper-no-swiping' : ''}>
              <div className="slide-content swiper-zoom-container">{slide}</div>
            </SwiperSlide>
          ))}
        </Swiper>

        {swiperSlides.length > 1 && (
          <Desktop>
            <SwiperButtonNext className={`swiper-button-next-${code}`}>
              <Icon icon={ChevronRight} color={'#101828'} width={30.67} height={30.67} />
            </SwiperButtonNext>
            <SwiperButtonPrev className={`swiper-button-prev-${code}`}>
              <Icon icon={ChevronRight} color={'#101828'} width={30.67} height={30.67} />
            </SwiperButtonPrev>
          </Desktop>
        )}
        <Mobile>
          <TabInfo activeTab={activeTab} currentIndex={currentSlide} totalPictures={photos.length} />
        </Mobile>
      </Content>
      <Footer>
        {/* TODO: currentIndex will need refactoring when videos and planimetry will be available */}
        <Desktop>
          <TabInfo activeTab={activeTab} currentIndex={currentSlide} totalPictures={photos.length} />
        </Desktop>
        <Mobile>
          <DetailsButton url={url} />
        </Mobile>
        <TabControls>
          {tabs.map(tab => (
            <TabButton key={tab} onClick={() => switchTab(tab)} $active={tab === activeTab}>
              <Icon
                color={tab === activeTab ? 'white' : '#667085'}
                icon={getTabInfo(tab)?.icon}
                width={20}
                height={20}
              />
              <span className="label">{getTabInfo(tab)?.label}</span>
            </TabButton>
          ))}
        </TabControls>
      </Footer>
    </Wrapper>
  );
};

interface TabsModalProps {
  tabs: Tab[];
  activeTab: Tab;
  switchTab: Dispatch<React.SetStateAction<Tab>>;
  close: () => void;
}

const DetailsButton: FC<{ url: string }> = ({ url }) => {
  const { t } = useAppTranslation();

  return (
    <PrimaryButton as={Link} to={url}>
      {t('plp.card.modal.open_space_details' as TTranslation)}
    </PrimaryButton>
  );
};
